// Portfolio Summary Calculator Functions
// Handles aggregations, P&L reconstruction, and metrics calculation

import type {
  AggregateGreeks,
  SectorExposure,
  AssetClassExposure,
  PositionSummary,
  EquityPoint,
  EquityPointHolding,
  ExternalCashFlow,
  DailyPLPoint,
  PortfolioMetrics,
  OptionSnapshotWithGreeks,
  SectorData,
  ExposureBreakdown,
  GreekBreakdownRow,
  GreekPositionBreakdown,
  GreekTradeType,
  CorrelationMatrix,
  ExposureConstituent,
  PortfolioPriceCoverage,
} from './summary.types.ts';
import type { HistoricalClose, HistoricalMarkConfidence, HistoricalMarkSource } from '../../types.ts';
import type { AccountHolding, AccountActivity, TradeMatchingResult } from '../brokerage/brokerage.types.ts';
import { EXPOSURE_CHART_COLORS, getSectorColors } from './summary.types.ts';

const ETF_SYMBOLS = new Set([
  'SPX', 'SPXW', 'SPY', 'QQQ', 'QQQM', 'IWM', 'DIA', 'VTI', 'VOO', 'VOOG',
  'VEA', 'VWO', 'EFA', 'EEM', 'ICLN', 'GDX', 'TLT', 'IEF', 'SHY', 'BND',
  'GLD', 'SLV', 'XLK', 'XLF', 'XLE', 'XLV',
]);

const MARKET_CAP_BUCKETS = [
  { name: 'Mega Cap', minMarketCap: 200_000_000_000 },
  { name: 'Large Cap', minMarketCap: 10_000_000_000 },
  { name: 'Mid Cap', minMarketCap: 2_000_000_000 },
  { name: 'Small Cap', minMarketCap: 300_000_000 },
  { name: 'Micro Cap', minMarketCap: 0 },
] as const;

const OFFICIAL_GICS_SECTORS = new Set([
  'Communication Services',
  'Consumer Discretionary',
  'Consumer Staples',
  'Energy',
  'Financials',
  'Health Care',
  'Industrials',
  'Information Technology',
  'Materials',
  'Real Estate',
  'Utilities',
]);

const SYMBOL_TO_GEOGRAPHY: Record<string, string> = {
  SPX: 'United States',
  SPXW: 'United States',
  SPY: 'United States',
  VOO: 'United States',
  VOOG: 'United States',
  QQQ: 'United States',
  QQQM: 'United States',
  DIA: 'United States',
  IWM: 'United States',
  VTI: 'United States',
  EFA: 'International Developed',
  VEA: 'International Developed',
  EEM: 'Emerging Markets',
  VWO: 'Emerging Markets',
  BOXX: 'Cash & equivalents',
  TLT: 'Cash & equivalents',
  IEF: 'Cash & equivalents',
  SHY: 'Cash & equivalents',
  BND: 'Cash & equivalents',
  GDX: 'Global / Thematic',
  GLD: 'Global / Thematic',
  SLV: 'Global / Thematic',
  ICLN: 'Global / Thematic',
};

interface OptionSpreadGroup {
  key: string;
  underlying: string;
  expirationDate: string;
  optionType: string;
  legs: AccountHolding[];
}

interface TickerExposureGroup {
  symbol: string;
  holdings: AccountHolding[];
}

interface ExposureAccumulator {
  mv: number;
  count: number;
  constituents: Map<string, ExposureConstituentAccumulator>;
}

interface ExposureConstituentAccumulator {
  ticker: string;
  name: string;
  mv: number;
  count: number;
}

interface HistoricalPosition {
  symbol: string;
  underlyingSymbol: string | null;
  isOption: boolean;
  units: number;
  openDate: string;
  closeDate: string | null;
  name: string;
}

interface ValuedPosition {
  value: number;
  missingSymbol: string | null;
  markSource: HistoricalMarkSource | null;
  markConfidence: HistoricalMarkConfidence | null;
  markDate: string | null;
}

const UNIT_EPSILON = 1e-8;
const OPTION_FALLBACK_MAX_GAP_DAYS = 10;

/**
 * Calculate net liquidation value
 */
export function calculateNetLiq(holdings: AccountHolding[], cash: number): number {
  const nonCashHoldingsValue = holdings
    .filter(h => !h.isCash)
    .reduce((sum, h) => sum + (h.marketValue || 0), 0);
  return nonCashHoldingsValue + cash;
}

/**
 * Calculate total market value (excluding cash)
 */
export function calculateTotalMV(holdings: AccountHolding[]): number {
  return holdings.filter(h => !h.isCash).reduce((sum, h) => sum + Math.abs(h.marketValue || 0), 0);
}

export function calculateAllocationMV(holdings: AccountHolding[]): number {
  return groupHoldingsByTicker(holdings)
    .reduce((sum, group) => sum + getTickerNetMarketValue(group), 0);
}

/**
 * Calculate unrealized P&L
 */
export function calculateUnrealized(holdings: AccountHolding[]): { pnl: number; pct: number } {
  let totalCost = 0;
  let totalValue = 0;

  for (const h of holdings) {
    if (h.isCash) continue;

    const cost = h.costBasis ?? ((h.averagePurchasePrice || 0) * h.units);
    const value = h.marketValue || 0;

    totalCost += cost;
    totalValue += value;
  }

  const pnl = totalValue - totalCost;
  const pct = totalCost !== 0 ? (pnl / Math.abs(totalCost)) * 100 : 0;

  return { pnl, pct };
}

/**
 * Aggregate Greeks from option snapshots
 */
export function aggregateGreeks(
  holdings: AccountHolding[],
  snapshots: Map<string, OptionSnapshotWithGreeks>,
  context = 'portfolio'
): AggregateGreeks {
  const result: AggregateGreeks = {
    delta: 0,
    deltaDollar: 0,
    gamma: 0,
    gammaDollar: 0,
    theta: 0,
    vega: 0,
    rho: 0,
  };
  const debugRows: GreekAggregationDebugRow[] = [];
  let runningDelta = 0;

  for (const holding of holdings) {
    if (!holding.isOption || !holding.optionSymbol) continue;

    const snapshot = snapshots.get(holding.optionSymbol);
    if (!snapshot?.greeks) {
      debugRows.push({
        optionSymbol: holding.optionSymbol,
        underlying: holding.underlyingSymbol || holding.symbol,
        contracts: holding.units,
        massiveDelta: null,
        underlyingPrice: null,
        contractMultiplier: 100,
        formula: 'missing',
        deltaContribution: 0,
        runningDelta,
        priorNotionalDelta: 0,
        note: 'Missing option snapshot or Greeks',
      });
      continue;
    }

    const { greeks, underlyingPrice } = snapshot;
    const multiplier = holding.units * 100; // Signed contract exposure. Short options are negative.
    const delta = finiteNumber(greeks.delta);
    const gamma = finiteNumber(greeks.gamma);
    const theta = finiteNumber(greeks.theta);
    const vega = finiteNumber(greeks.vega);
    const rho = finiteNumber(greeks.rho);
    const underlying = finiteNumber(underlyingPrice);
    const deltaContribution = delta * multiplier;
    runningDelta += deltaContribution;

    // Delta is already the dollar change per share for a $1 underlying move.
    // Multiply only by signed contracts and OCC contract size.
    result.delta += deltaContribution;
    result.deltaDollar += deltaContribution;

    if (underlying > 0) {
      result.gammaDollar += 0.5 * gamma * Math.pow(underlying * 0.01, 2) * multiplier;
    }

    result.gamma += gamma * multiplier;
    result.theta += theta * multiplier;
    result.vega += vega * multiplier;
    result.rho += rho * multiplier;

    debugRows.push({
      optionSymbol: holding.optionSymbol,
      underlying: snapshot.underlying,
      contracts: holding.units,
      massiveDelta: delta,
      underlyingPrice: underlying || null,
      contractMultiplier: 100,
      formula: `${delta} * ${holding.units} * 100`,
      deltaContribution,
      runningDelta,
      priorNotionalDelta: underlying > 0 ? deltaContribution * underlying : 0,
      note: Math.abs(delta) > 1 ? 'Massive delta magnitude is greater than 1; check source scale' : '',
    });
  }

  if (context === 'portfolio total') {
    logGreekAggregation(context, debugRows, result);
  }

  return result;
}

function finiteNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

interface GreekAggregationDebugRow {
  optionSymbol: string;
  underlying: string;
  contracts: number;
  massiveDelta: number | null;
  underlyingPrice: number | null;
  contractMultiplier: number;
  formula: string;
  deltaContribution: number;
  runningDelta: number;
  priorNotionalDelta: number;
  note: string;
}

function logGreekAggregation(
  context: string,
  rows: GreekAggregationDebugRow[],
  totals: AggregateGreeks
): void {
  if (typeof window === 'undefined') return;

  console.group(`[Portfolio Analysis] Greek delta calculation: ${context}`);
  console.info('Delta formula: Massive delta * signed option contracts * 100. This excludes equities and does not multiply by underlying price.');
  console.table(rows);
  console.log('Totals', {
    delta: totals.delta,
    deltaDollarPerOneDollarMove: totals.deltaDollar,
    gamma: totals.gamma,
    gammaDollar: totals.gammaDollar,
    theta: totals.theta,
    vega: totals.vega,
    rho: totals.rho,
  });
  console.log('Largest absolute delta contributors', [...rows]
    .sort((left, right) => Math.abs(right.deltaContribution) - Math.abs(left.deltaContribution))
    .slice(0, 10));
  console.groupEnd();
}

/**
 * Group holdings by sector
 */
export function groupBySector(
  holdings: AccountHolding[],
  sectorData: Map<string, SectorData>,
  totalMV: number
): SectorExposure[] {
  const sectorMap = new Map<string, ExposureAccumulator>();

  for (const group of groupHoldingsByTicker(holdings)) {
    const symbol = group.symbol;
    const data = sectorData.get(symbol.toUpperCase());
    const marketValue = getTickerNetMarketValue(group);
    if (marketValue <= 0) continue;

    const allocations = normalizeSectorAllocations(data?.sectorAllocations);
    const constituent = exposureConstituentFromGroup(group);
    const count = group.holdings.length;

    if (allocations) {
      for (const [sector, weight] of Object.entries(allocations)) {
        addExposure(sectorMap, sector, marketValue * weight, count, constituent);
      }
    } else {
      addExposure(sectorMap, normalizeSectorName(data?.sector || 'Unknown'), marketValue, count, constituent);
    }
  }

  // Convert to array and sort by value
  const sectors = Array.from(sectorMap.entries())
    .map(([name, data]) => ({
      name,
      mv: data.mv,
      pct: totalMV > 0 ? (data.mv / totalMV) * 100 : 0,
      benchmarkPct: 0,
      positionCount: data.count,
      constituents: buildConstituents(data.constituents, totalMV),
      color: '', // Will be assigned below
    }))
    .sort((a, b) => b.mv - a.mv);

  // Assign colors
  const colors = getSectorColors(sectors.length);
  sectors.forEach((s, i) => {
    s.color = colors[i];
  });

  return sectors;
}

function normalizeSectorAllocations(allocation: Record<string, number> | undefined): Record<string, number> | null {
  if (!allocation) return null;

  const entries = Object.entries(allocation)
    .map(([sector, weight]) => [normalizeSectorName(sector), Number(weight)] as const)
    .filter(([, weight]) => Number.isFinite(weight) && weight > 0);
  const total = entries.reduce((sum, [, weight]) => sum + weight, 0);
  if (total <= 0) return null;

  return Object.fromEntries(entries.map(([sector, weight]) => [sector, weight / total]));
}

function normalizeSectorName(sector: string): string {
  if (sector === 'Technology') return 'Information Technology';
  if (sector === 'Healthcare') return 'Health Care';
  if (sector === 'Communication') return 'Communication Services';
  if (OFFICIAL_GICS_SECTORS.has(sector)) return sector;
  return 'Unknown';
}

export function addCashExposure<T extends SectorExposure | ExposureBreakdown>(
  rows: T[],
  cash: number,
  denominator: number,
  color = '#6b7280'
): T[] {
  if (cash <= 0 || denominator <= 0) return rows;

  return [
    ...rows,
    {
      name: 'Cash',
      mv: cash,
      pct: (cash / denominator) * 100,
      benchmarkPct: 0,
      positionCount: 1,
      constituents: [{
        ticker: 'Cash',
        name: 'Cash',
        mv: cash,
        pct: (cash / denominator) * 100,
        positionCount: 1,
      }],
      color,
    } as T,
  ].sort((a, b) => b.mv - a.mv);
}

export function groupByGeography(
  holdings: AccountHolding[],
  sectorData: Map<string, SectorData>,
  denominator: number
): ExposureBreakdown[] {
  const geographyMap = new Map<string, ExposureAccumulator>();

  for (const group of groupHoldingsByTicker(holdings)) {
    const symbol = group.symbol;
    const rawCountry = sectorData.get(symbol.toUpperCase())?.country || 'Unknown';
    const geography = normalizeGeography(rawCountry, symbol);
    const marketValue = getTickerNetMarketValue(group);
    if (marketValue <= 0) continue;

    addExposure(geographyMap, geography, marketValue, group.holdings.length, exposureConstituentFromGroup(group));
  }

  return buildExposureRows(geographyMap, denominator);
}

export function groupByMarketCap(
  holdings: AccountHolding[],
  sectorData: Map<string, SectorData>,
  denominator: number
): ExposureBreakdown[] {
  const marketCapMap = new Map<string, ExposureAccumulator>();

  for (const group of groupHoldingsByTicker(holdings)) {
    const symbol = group.symbol;
    const bucket = classifyMarketCap(symbol, sectorData.get(symbol));
    const marketValue = getTickerNetMarketValue(group);
    if (marketValue <= 0) continue;

    addExposure(marketCapMap, bucket, marketValue, group.holdings.length, exposureConstituentFromGroup(group));
  }

  return buildExposureRows(marketCapMap, denominator);
}

function buildExposureRows(
  source: Map<string, ExposureAccumulator>,
  denominator: number
): ExposureBreakdown[] {
  return Array.from(source.entries())
    .map(([name, data], i) => ({
      name,
      mv: data.mv,
      pct: denominator > 0 ? (data.mv / denominator) * 100 : 0,
      benchmarkPct: 0,
      positionCount: data.count,
      constituents: buildConstituents(data.constituents, denominator),
      color: EXPOSURE_CHART_COLORS[i % EXPOSURE_CHART_COLORS.length],
    }))
    .sort((a, b) => b.mv - a.mv);
}

function addExposure(
  source: Map<string, ExposureAccumulator>,
  bucket: string,
  marketValue: number,
  count: number,
  constituent: ExposureConstituentAccumulator
): void {
  const existing = source.get(bucket) || { mv: 0, count: 0, constituents: new Map<string, ExposureConstituentAccumulator>() };
  existing.mv += marketValue;
  existing.count += count;

  const existingConstituent = existing.constituents.get(constituent.ticker) || {
    ticker: constituent.ticker,
    name: constituent.name,
    mv: 0,
    count: 0,
  };
  existingConstituent.mv += marketValue;
  existingConstituent.count += count;
  existing.constituents.set(constituent.ticker, existingConstituent);
  source.set(bucket, existing);
}

function exposureConstituentFromGroup(group: TickerExposureGroup): ExposureConstituentAccumulator {
  const stockHolding = group.holdings.find(holding => !holding.isOption);
  const representative = stockHolding || group.holdings[0];

  return {
    ticker: group.symbol,
    name: stockHolding?.description || representative?.description || `${group.symbol} options`,
    mv: 0,
    count: 0,
  };
}

function buildConstituents(
  constituents: Map<string, ExposureConstituentAccumulator>,
  denominator: number
): ExposureConstituent[] {
  return Array.from(constituents.values())
    .map(item => ({
      ticker: item.ticker,
      name: item.name,
      mv: item.mv,
      pct: denominator > 0 ? (item.mv / denominator) * 100 : 0,
      positionCount: item.count,
    }))
    .sort((a, b) => b.mv - a.mv);
}

function normalizeGeography(country: string, symbol: string): string {
  const upperSymbol = symbol.toUpperCase();
  const mapped = SYMBOL_TO_GEOGRAPHY[upperSymbol];
  if (mapped) return mapped;

  const normalized = country.toLowerCase();
  if (normalized === 'us' || normalized === 'usa' || normalized === 'united states') return 'United States';
  if (normalized.includes('canada') || normalized.includes('lat')) return 'Canada / LatAm';
  if (normalized.includes('united kingdom') || normalized === 'gb' || normalized === 'uk') return 'United Kingdom';
  if (normalized.includes('china') || normalized.includes('japan') || normalized.includes('asia')) return 'Asia-Pacific';
  if (normalized.includes('europe') || normalized.includes('germany') || normalized.includes('france')) return 'Europe ex-UK';
  if ((normalized === 'unknown' || normalized === '') && /^[A-Z.-]{1,5}$/.test(upperSymbol)) return 'United States';
  return country || 'Unknown';
}

function classifyMarketCap(symbol: string, metadata: SectorData | undefined): string {
  const rawSector = metadata?.sector || '';
  if (rawSector === 'Cash & equivalents' || rawSector === 'Fixed Income' || rawSector === 'Crypto / Alternatives') {
    return rawSector;
  }

  if (ETF_SYMBOLS.has(symbol)) return 'ETF / Fund';
  if (metadata?.sectorAllocations || rawSector === 'ETF / Index') return 'ETF / Fund';

  const marketCap = metadata?.marketCap;
  if (marketCap == null || marketCap < 0) return 'Unknown';

  const bucket = MARKET_CAP_BUCKETS.find(item => marketCap >= item.minMarketCap);
  if (bucket) return bucket.name;

  return 'Unknown';
}

/**
 * Group holdings by asset class
 */
export function groupByAssetClass(holdings: AccountHolding[], cash: number): AssetClassExposure[] {
  let equities = 0;
  let optionsLong = 0;
  let optionsShort = 0;

  for (const holding of holdings) {
    if (holding.isCash) continue;

    const value = Math.abs(holding.marketValue || 0);

    if (holding.isOption) {
      if (holding.units >= 0) {
        optionsLong += value;
      } else {
        optionsShort += value;
      }
    } else {
      equities += value;
    }
  }

  const total = equities + optionsLong + optionsShort + Math.abs(cash);

  const classes: AssetClassExposure[] = [];

  if (equities > 0) {
    classes.push({
      name: 'Equities',
      mv: equities,
      pct: total > 0 ? (equities / total) * 100 : 0,
      color: '#3b82f6', // Blue
    });
  }

  if (optionsLong > 0) {
    classes.push({
      name: 'Options (Long)',
      mv: optionsLong,
      pct: total > 0 ? (optionsLong / total) * 100 : 0,
      color: '#22c55e', // Green
    });
  }

  if (optionsShort > 0) {
    classes.push({
      name: 'Options (Short)',
      mv: optionsShort,
      pct: total > 0 ? (optionsShort / total) * 100 : 0,
      color: '#ef4444', // Red
    });
  }

  if (cash > 0) {
    classes.push({
      name: 'Cash',
      mv: cash,
      pct: total > 0 ? (cash / total) * 100 : 0,
      color: '#a3a3a3', // Gray
    });
  }

  return classes.sort((a, b) => b.mv - a.mv);
}

/**
 * Get top positions sorted by market value
 */
export function getTopPositions(
  holdings: AccountHolding[],
  sectorData: Map<string, SectorData>,
  snapshots: Map<string, OptionSnapshotWithGreeks>,
  totalMV: number,
  limit: number = 10
): PositionSummary[] {
  const spreadLegs = new Set(findOptionSpreadGroups(holdings).flatMap(group => group.legs));

  return groupHoldingsByTicker(holdings)
    .map(group => summarizeTickerExposure(group, spreadLegs, sectorData, snapshots, totalMV))
    .sort((a, b) => b.mv - a.mv)
    .slice(0, limit);
}

export function calculateGreekBreakdown(
  holdings: AccountHolding[],
  snapshots: Map<string, OptionSnapshotWithGreeks>
): GreekBreakdownRow[] {
  const spreadLegs = new Set(findOptionSpreadGroups(holdings).flatMap(group => group.legs));
  const buckets: Record<GreekTradeType, AccountHolding[]> = {
    equity: [],
    option: [],
    spread: [],
  };

  for (const holding of holdings) {
    if (holding.isCash) continue;
    if (!holding.isOption) {
      buckets.equity.push(holding);
      continue;
    }

    if (spreadLegs.has(holding)) {
      buckets.spread.push(holding);
    } else {
      buckets.option.push(holding);
    }
  }

  return [
    {
      type: 'equity',
      label: 'Equity',
      count: buckets.equity.length,
      greeks: aggregateGreeks(buckets.equity, snapshots, 'equity bucket'),
      positions: [],
    },
    {
      type: 'option',
      label: 'Single-leg options',
      count: buckets.option.length,
      greeks: aggregateGreeks(buckets.option, snapshots, 'single-leg options bucket'),
      positions: buildGreekPositionBreakdown(buckets.option, snapshots),
    },
    {
      type: 'spread',
      label: 'Spreads & collars',
      count: buckets.spread.length,
      greeks: aggregateGreeks(buckets.spread, snapshots, 'spreads bucket'),
      positions: buildGreekPositionBreakdown(buckets.spread, snapshots),
    },
  ];
}

function buildGreekPositionBreakdown(
  holdings: AccountHolding[],
  snapshots: Map<string, OptionSnapshotWithGreeks>
): GreekPositionBreakdown[] {
  return holdings
    .map(holding => {
      const snapshot = holding.optionSymbol ? snapshots.get(holding.optionSymbol) : null;
      const greeks = aggregateGreeks([holding], snapshots, 'position detail');

      return {
        symbol: holding.optionSymbol || holding.symbol,
        name: holding.description || holding.optionSymbol || holding.symbol,
        underlyingSymbol: snapshot?.underlying || holding.underlyingSymbol || holding.symbol,
        contracts: holding.units,
        marketValue: holding.marketValue || 0,
        greeks,
        hasGreeks: Boolean(snapshot?.greeks),
      };
    })
    .sort((left, right) => {
      const deltaDiff = Math.abs(right.greeks.deltaDollar) - Math.abs(left.greeks.deltaDollar);
      if (deltaDiff !== 0) return deltaDiff;
      return left.symbol.localeCompare(right.symbol);
    });
}

function summarizeTickerExposure(
  group: TickerExposureGroup,
  spreadLegs: Set<AccountHolding>,
  sectorData: Map<string, SectorData>,
  snapshots: Map<string, OptionSnapshotWithGreeks>,
  totalMV: number
): PositionSummary {
  const sector = sectorData.get(group.symbol)?.sector || 'Unknown';
  const netMarketValue = group.holdings.reduce((sum, holding) => sum + (holding.marketValue || 0), 0);
  const mv = Math.abs(netMarketValue);
  const dayPL = group.holdings.reduce((sum, holding) => sum + (holding.unrealizedPnl || 0), 0);
  const costBasis = group.holdings.reduce(
    (sum, holding) => sum + Math.abs(holding.costBasis ?? ((holding.averagePurchasePrice || 0) * holding.units)),
    0
  );
  const greeks = group.holdings.reduce(
    (sum, holding) => {
      const holdingGreeks = getHoldingGreekExposure(holding, snapshots);
      sum.delta += holdingGreeks.delta;
      sum.theta += holdingGreeks.theta;
      return sum;
    },
    { delta: 0, theta: 0 }
  );
  const hasEquity = group.holdings.some(holding => !holding.isOption);
  const hasOption = group.holdings.some(holding => holding.isOption);
  const hasSpread = group.holdings.some(holding => spreadLegs.has(holding));

  return {
    ticker: group.symbol,
    name: formatTickerExposureName(group, hasOption, hasSpread),
    sector,
    mv,
    pct: totalMV > 0 ? (mv / totalMV) * 100 : 0,
    dayPL,
    dayPLPct: costBasis > 0 ? (dayPL / costBasis) * 100 : 0,
    delta: greeks.delta,
    theta: greeks.theta,
    isOption: hasOption,
    strategyType: getTickerExposureStrategyType(hasEquity, hasOption, hasSpread),
  };
}

function getHoldingGreekExposure(
  holding: AccountHolding,
  snapshots: Map<string, OptionSnapshotWithGreeks>
): { delta: number; theta: number } {
  if (holding.isOption && holding.optionSymbol) {
    const snapshot = snapshots.get(holding.optionSymbol);
    if (snapshot?.greeks) {
      const multiplier = holding.units * 100;
      return {
        delta: finiteNumber(snapshot.greeks.delta) * multiplier,
        theta: finiteNumber(snapshot.greeks.theta) * multiplier,
      };
    }
    return { delta: 0, theta: 0 };
  }

  return { delta: holding.isCash ? 0 : holding.units, theta: 0 };
}

function groupHoldingsByTicker(holdings: AccountHolding[]): TickerExposureGroup[] {
  const groups = new Map<string, AccountHolding[]>();

  for (const holding of holdings) {
    if (holding.isCash) continue;
    const symbol = (holding.underlyingSymbol || holding.symbol).toUpperCase();
    const existing = groups.get(symbol) || [];
    existing.push(holding);
    groups.set(symbol, existing);
  }

  return [...groups.entries()].map(([symbol, groupHoldings]) => ({
    symbol,
    holdings: groupHoldings,
  }));
}

function getTickerNetMarketValue(group: TickerExposureGroup): number {
  const netMarketValue = group.holdings.reduce((sum, holding) => sum + (holding.marketValue || 0), 0);
  return Math.abs(netMarketValue);
}

function findOptionSpreadGroups(holdings: AccountHolding[]): OptionSpreadGroup[] {
  const optionLegsByKey = new Map<string, AccountHolding[]>();

  for (const holding of holdings) {
    if (!holding.isOption || holding.isCash || !holding.expirationDate) continue;
    const underlying = (holding.underlyingSymbol || holding.symbol).toUpperCase();
    const key = `${underlying}|${holding.expirationDate}`;
    const existing = optionLegsByKey.get(key) || [];
    existing.push(holding);
    optionLegsByKey.set(key, existing);
  }

  const groups: OptionSpreadGroup[] = [];
  for (const [key, legs] of optionLegsByKey) {
    const [underlying, expirationDate] = key.split('|');
    const uniqueContracts = new Set(legs.map(leg => leg.optionSymbol || leg.symbol));
    const hasLong = legs.some(leg => leg.units > 0);
    const hasShort = legs.some(leg => leg.units < 0);
    if (uniqueContracts.size > 1 && hasLong && hasShort) {
      const uniqueTypes = [...new Set(legs.map(leg => leg.optionType).filter(Boolean))];
      groups.push({
        key,
        underlying,
        expirationDate,
        optionType: uniqueTypes.length === 1 ? uniqueTypes[0]! : 'OPTION',
        legs,
      });
    }
  }

  return groups;
}

function formatTickerExposureName(
  group: TickerExposureGroup,
  hasOption: boolean,
  hasSpread: boolean
): string {
  const stockHolding = group.holdings.find(holding => !holding.isOption);
  if (stockHolding?.description) {
    return hasOption ? `${stockHolding.description} + options` : stockHolding.description;
  }
  if (hasOption && group.holdings.length === 1) {
    return formatOptionName(group.holdings[0]);
  }
  if (hasSpread) return `${group.symbol} options and spreads`;
  if (hasOption) return `${group.symbol} options`;
  return group.symbol;
}

function getTickerExposureStrategyType(hasEquity: boolean, hasOption: boolean, hasSpread: boolean): string {
  if (hasEquity && hasOption) return hasSpread ? 'Stock + Options / Spreads' : 'Stock + Options';
  if (hasOption) return hasSpread ? 'Options / Spreads' : 'Option';
  return 'Stock';
}

function formatOptionName(h: AccountHolding): string {
  if (!h.strikePrice || !h.expirationDate || !h.optionType) {
    return h.description || 'Option';
  }

  const expDate = new Date(h.expirationDate);
  const month = expDate.toLocaleString('en-US', { month: 'short' });
  const day = expDate.getDate();
  const year = expDate.getFullYear() % 100;

  return `${h.optionType} $${h.strikePrice} ${month} ${day} '${year}`;
}

/**
 * Reconstruct equity curve from trade activities
 * Uses a simplified approach: start from current value and work backwards
 */
export function reconstructEquityCurve(
  activities: AccountActivity[],
  currentValue: number,
  days: number = 90
): EquityPoint[] {
  const points: EquityPoint[] = [];
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - days);

  // Sort activities by date (newest first for backward reconstruction)
  const sortedActivities = [...activities]
    .filter(a => new Date(a.tradeDate) >= startDate)
    .sort((a, b) => new Date(b.tradeDate).getTime() - new Date(a.tradeDate).getTime());

  // Build day-by-day cash flow map
  const cashFlowByDate = new Map<string, number>();

  for (const activity of sortedActivities) {
    const dateKey = activity.tradeDate.split('T')[0];
    const existing = cashFlowByDate.get(dateKey) || 0;

    // Cash flows: positive for sales/dividends, negative for buys
    let cashFlow = 0;
    if (activity.type === 'BUY') {
      cashFlow = -(activity.amount || 0);
    } else if (activity.type === 'SELL') {
      cashFlow = activity.amount || 0;
    } else if (activity.type === 'DIVIDEND') {
      cashFlow = activity.amount || 0;
    } else if (activity.type === 'DEPOSIT') {
      // Deposits are not part of trading P&L, but we track them to adjust baseline
      cashFlow = activity.amount || 0;
    } else if (activity.type === 'WITHDRAWAL') {
      cashFlow = -(activity.amount || 0);
    }

    cashFlowByDate.set(dateKey, existing + cashFlow);
  }

  // Generate daily points (working backward from current value)
  let runningValue = currentValue;

  for (let i = 0; i <= days; i++) {
    const date = new Date(endDate);
    date.setDate(date.getDate() - i);
    const dateKey = date.toISOString().split('T')[0];

    // Skip weekends for cleaner chart
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;

    points.unshift({
      date: dateKey,
      value: runningValue,
    });

    // Adjust for cash flow on this day (subtract to go backward)
    const cashFlow = cashFlowByDate.get(dateKey) || 0;
    runningValue -= cashFlow;
  }

  return points;
}

export function buildHoldingsEquityCurve(
  holdings: AccountHolding[],
  cash: number,
  historicalPrices: Record<string, HistoricalClose[]>,
  days: number = 365
): EquityPoint[] {
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - days);
  const startKey = startDate.toISOString().split('T')[0];
  const endKey = endDate.toISOString().split('T')[0];
  const dateKeys = getHistoricalDateKeys(historicalPrices, startKey, endKey);

  if (!dateKeys.includes(endKey)) {
    dateKeys.push(endKey);
  }
  const currentValue = calculateNetLiq(holdings, cash);

  return dateKeys
    .sort()
    .map(date => ({
      date,
      value: date === endKey
        ? currentValue
        : valueHoldingsOnDate(holdings, cash, historicalPrices, date),
    }))
    .filter(point => Number.isFinite(point.value));
}

export function buildTransactionEquityCurve(
  holdings: AccountHolding[],
  cash: number,
  activities: AccountActivity[],
  historicalPrices: Record<string, HistoricalClose[]>,
  tradeResults: TradeMatchingResult,
  days: number = 365,
  asOfDate: Date | string = new Date(),
  includeHoldings = false
): EquityPoint[] {
  const actualCurve = buildActualTransactionEquityCurve(
    holdings,
    cash,
    activities,
    historicalPrices,
    tradeResults,
    days,
    asOfDate,
    includeHoldings
  );

  return buildCashFlowExcludedEquityCurve(actualCurve, activities);
}

export function buildActualTransactionEquityCurve(
  holdings: AccountHolding[],
  cash: number,
  activities: AccountActivity[],
  historicalPrices: Record<string, HistoricalClose[]>,
  tradeResults: TradeMatchingResult,
  days: number = 365,
  asOfDate: Date | string = new Date(),
  includeHoldings = false
): EquityPoint[] {
  const endKey = dateKey(typeof asOfDate === 'string' ? asOfDate : asOfDate.toISOString());
  const startDate = new Date(`${endKey}T00:00:00Z`);
  startDate.setUTCDate(startDate.getUTCDate() - days);
  const startKey = startDate.toISOString().split('T')[0];
  const dateKeys = getHistoricalDateKeys(historicalPrices, startKey, endKey);

  if (!dateKeys.includes(endKey)) {
    dateKeys.push(endKey);
  }

  const positions = buildHistoricalPositionsFromTrades(tradeResults, holdings, startKey);
  const points: EquityPoint[] = [];

  for (const date of dateKeys.sort()) {
    if (date === endKey) {
      points.push({
        date,
        value: calculateNetLiq(holdings, cash),
        ...(includeHoldings ? { holdings: buildCurrentHoldingRows(holdings, cash, endKey) } : {}),
      });
      continue;
    }

    let value = estimateCashOnDate(cash, activities, date);
    const holdingRows: EquityPointHolding[] = [];
    if (includeHoldings) {
      holdingRows.push(buildCashHoldingRow(value, date));
    }
    let missingRequiredPrice = false;

    for (const position of positionsHeldOnDate(positions, date)) {
      const valued = valueHistoricalPosition(position, historicalPrices, date);
      if (valued.missingSymbol) {
        missingRequiredPrice = true;
        break;
      }
      value += valued.value;
      if (includeHoldings) {
        addHoldingRow(holdingRows, {
          symbol: position.symbol,
          name: position.name,
          type: position.isOption ? 'option' : 'equity',
          quantity: position.units,
          value: valued.value,
          ...(valued.markSource ? { markSource: valued.markSource } : {}),
          ...(valued.markConfidence ? { markConfidence: valued.markConfidence } : {}),
          ...(valued.markDate ? { markDate: valued.markDate } : {}),
        });
      }
    }

    if (!missingRequiredPrice && Number.isFinite(value)) {
      points.push({
        date,
        value,
        ...(includeHoldings ? { holdings: normalizeHoldingRows(holdingRows) } : {}),
      });
    }
  }

  return points;
}

export function calculateHistoricalMarkCoverage(
  holdings: AccountHolding[],
  historicalPrices: Record<string, HistoricalClose[]>,
  tradeResults: TradeMatchingResult,
  equityCurve: EquityPoint[],
  days: number = 365,
  asOfDate: Date | string = new Date()
): PortfolioPriceCoverage {
  const endKey = dateKey(typeof asOfDate === 'string' ? asOfDate : asOfDate.toISOString());
  const startDate = new Date(`${endKey}T00:00:00Z`);
  startDate.setUTCDate(startDate.getUTCDate() - days);
  const startKey = startDate.toISOString().split('T')[0];
  const candidateDates = getHistoricalDateKeys(historicalPrices, startKey, endKey);
  if (!candidateDates.includes(endKey)) {
    candidateDates.push(endKey);
  }

  const positions = buildHistoricalPositionsFromTrades(tradeResults, holdings, startKey);
  const skippedSymbols = new Map<string, number>();
  const skippedRequiredSymbols = new Map<string, { count: number; type: 'equity' | 'option' }>();
  const sourceCounts: PortfolioPriceCoverage['sourceCounts'] = {};
  let skippedOptionMarkDates = 0;
  let totalOptionMarks = 0;
  let lowConfidenceOptionMarks = 0;
  let fallbackOptionMarks = 0;

  for (const date of candidateDates) {
    if (date === endKey) continue;
    const heldPositions = positionsHeldOnDate(positions, date);
    let missingDate = false;

    for (const position of heldPositions) {
      const valued = valueHistoricalPosition(position, historicalPrices, date);
      if (position.isOption) {
        totalOptionMarks += 1;
      }

      if (valued.missingSymbol) {
        missingDate = true;
        skippedSymbols.set(position.symbol, (skippedSymbols.get(position.symbol) || 0) + 1);
        const existing = skippedRequiredSymbols.get(valued.missingSymbol) || {
          count: 0,
          type: position.isOption ? 'option' as const : 'equity' as const,
        };
        existing.count += 1;
        skippedRequiredSymbols.set(valued.missingSymbol, existing);
        continue;
      }

      if (!position.isOption || !valued.markSource) continue;

      const source = valued.markSource;
      sourceCounts[source] = (sourceCounts[source] || 0) + 1;
      if (valued.markConfidence === 'low') {
        lowConfidenceOptionMarks += 1;
      }
      if (source === 'interpolated' || source === 'nearest_option_mark') {
        fallbackOptionMarks += 1;
      }
    }

    if (missingDate) {
      skippedOptionMarkDates += 1;
    }
  }

  return {
    candidateDates: candidateDates.length,
    renderedDates: equityCurve.length,
    skippedDates: Math.max(0, candidateDates.length - equityCurve.length),
    skippedOptionMarkDates,
    totalOptionMarks,
    lowConfidenceOptionMarks,
    fallbackOptionMarks,
    sourceCounts,
    skippedSymbols: [...skippedSymbols.entries()]
      .map(([symbol, count]) => ({ symbol, count }))
      .sort((a, b) => b.count - a.count || a.symbol.localeCompare(b.symbol))
      .slice(0, 12),
    skippedRequiredSymbols: [...skippedRequiredSymbols.entries()]
      .map(([symbol, item]) => ({ symbol, count: item.count, type: item.type }))
      .sort((a, b) => b.count - a.count || a.symbol.localeCompare(b.symbol))
      .slice(0, 12),
  };
}

export function getRequiredOptionMarkDatesBySymbol(
  holdings: AccountHolding[],
  tradeResults: TradeMatchingResult,
  candidateDates: string[],
  days: number = 365,
  asOfDate: Date | string = new Date()
): Map<string, string[]> {
  const endKey = dateKey(typeof asOfDate === 'string' ? asOfDate : asOfDate.toISOString());
  const startDate = new Date(`${endKey}T00:00:00Z`);
  startDate.setUTCDate(startDate.getUTCDate() - days);
  const startKey = startDate.toISOString().split('T')[0];
  const positions = buildHistoricalPositionsFromTrades(tradeResults, holdings, startKey);
  const datesBySymbol = new Map<string, Set<string>>();

  for (const date of candidateDates) {
    if (date === endKey) continue;
    for (const position of positionsHeldOnDate(positions, date)) {
      if (!position.isOption) continue;
      const dates = datesBySymbol.get(position.symbol) || new Set<string>();
      dates.add(date);
      datesBySymbol.set(position.symbol, dates);
    }
  }

  return new Map([...datesBySymbol.entries()]
    .map(([symbol, dates]) => [symbol, [...dates].sort()]));
}

export function buildCashFlowExcludedEquityCurve(
  actualCurve: EquityPoint[],
  activities: AccountActivity[]
): EquityPoint[] {
  if (actualCurve.length === 0) return [];

  const externalFlows = getExternalCashFlows(activities);
  if (externalFlows.length === 0) return actualCurve;

  return actualCurve.map(point => {
    const laterExternalFlow = externalFlows
      .filter(flow => flow.date > point.date)
      .reduce((sum, flow) => sum + flow.amount, 0);
    const holdings = point.holdings && laterExternalFlow !== 0
      ? normalizeHoldingRows([
        ...point.holdings,
        buildTransferAdjustmentRow(laterExternalFlow, point.date),
      ])
      : point.holdings;

    return {
      ...point,
      date: point.date,
      value: point.value + laterExternalFlow,
      ...(holdings ? { holdings } : {}),
    };
  });
}

export function logEquityCurveDiagnostics(
  holdings: AccountHolding[],
  historicalPrices: Record<string, HistoricalClose[]>,
  equityCurve: EquityPoint[]
): void {
  if (equityCurve.length < 2) return;

  const moves = equityCurve
    .slice(1)
    .map((point, index) => {
      const previous = equityCurve[index];
      const contributors = holdings
        .filter(holding => !holding.isCash)
        .map(holding => {
          const previousValue = estimateHoldingValueForCurveDiagnostics(holding, historicalPrices, previous.date);
          const currentValue = estimateHoldingValueForCurveDiagnostics(holding, historicalPrices, point.date);
          return {
            symbol: holding.optionSymbol || holding.symbol,
            underlying: holding.underlyingSymbol || holding.symbol,
            type: holding.isOption ? 'option' : 'equity',
            units: holding.units,
            previousValue,
            currentValue,
            delta: currentValue - previousValue,
            previousSource: describeHoldingCurveSource(holding, historicalPrices, previous.date),
            currentSource: describeHoldingCurveSource(holding, historicalPrices, point.date),
          };
        })
        .filter(row => Math.abs(row.delta) >= 1)
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
        .slice(0, 8);

      return {
        from: previous.date,
        to: point.date,
        previousValue: previous.value,
        currentValue: point.value,
        delta: point.value - previous.value,
        contributors,
      };
    })
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 8);

  console.groupCollapsed('[DYR equity debug] Largest holdings-derived curve moves');
  console.log('[DYR equity debug JSON] largest curve moves', JSON.stringify(moves));
  console.table(moves.map(move => ({
    from: move.from,
    to: move.to,
    delta: move.delta,
    topContributor: move.contributors[0]?.symbol || '',
    topContributorDelta: move.contributors[0]?.delta || 0,
    previousSource: move.contributors[0]?.previousSource || '',
    currentSource: move.contributors[0]?.currentSource || '',
  })));
  console.groupEnd();
}

export function buildCashFlowIncludedEquityCurve(
  transferNeutralCurve: EquityPoint[],
  activities: AccountActivity[]
): EquityPoint[] {
  if (transferNeutralCurve.length === 0) return [];

  const externalFlows = getExternalCashFlows(activities);

  if (externalFlows.length === 0) {
    return transferNeutralCurve;
  }

  return transferNeutralCurve.map(point => {
    const laterExternalFlow = externalFlows
      .filter(flow => flow.date > point.date)
      .reduce((sum, flow) => sum + flow.amount, 0);
    const holdings = point.holdings?.filter(holding => holding.type !== 'adjustment');

    return {
      ...point,
      date: point.date,
      value: point.value - laterExternalFlow,
      ...(holdings ? { holdings } : {}),
    };
  });
}

export function getExternalCashFlows(activities: AccountActivity[]): ExternalCashFlow[] {
  return activities
    .map(activity => ({
      date: activity.tradeDate?.split('T')[0] || '',
      amount: getExternalCashFlowAmount(activity),
      type: String(activity.type || ''),
      description: activity.description || '',
    }))
    .filter(flow => flow.date && flow.amount !== 0)
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function getExternalCashFlowAmount(activity: AccountActivity): number {
  const type = String(activity.type || '').toUpperCase();
  const rawAmount = finiteNumber(activity.amount);
  const transferredAssetValue = finiteNumber(activity.units) * finiteNumber(activity.price) * (activity.optionSymbol ? 100 : 1);
  const amount = rawAmount !== 0 ? rawAmount : transferredAssetValue;

  if (type === 'DEPOSIT' || type === 'CONTRIBUTION' || type === 'EXTERNAL_ASSET_TRANSFER_IN') {
    return Math.abs(amount);
  }

  if (type === 'WITHDRAWAL' || type === 'DISTRIBUTION' || type === 'EXTERNAL_ASSET_TRANSFER_OUT') {
    return -Math.abs(amount);
  }

  return 0;
}

function getHistoricalDateKeys(
  historicalPrices: Record<string, HistoricalClose[]>,
  startKey: string,
  endKey: string
): string[] {
  const dates = new Set<string>();
  const benchmarkDates = historicalPrices.SPY || [];
  const sourceHistories = benchmarkDates.length > 0
    ? [benchmarkDates]
    : Object.values(historicalPrices);

  for (const history of sourceHistories) {
    for (const point of history) {
      if (point.date >= startKey && point.date <= endKey && point.close > 0) {
        dates.add(point.date);
      }
    }
  }

  return [...dates];
}

function valueHoldingsOnDate(
  holdings: AccountHolding[],
  cash: number,
  historicalPrices: Record<string, HistoricalClose[]>,
  date: string
): number {
  let value = cash;

  for (const holding of holdings) {
    if (holding.isCash) continue;

    if (holding.isOption) {
      value += estimateOptionValueOnDate(holding, historicalPrices, date);
    } else {
      value += estimateEquityValueOnDate(holding, historicalPrices, date);
    }
  }

  return value;
}

function estimateEquityValueOnDate(
  holding: AccountHolding,
  historicalPrices: Record<string, HistoricalClose[]>,
  date: string
): number {
  const symbol = normalizeHistoricalSymbol(holding.symbol);
  const close = getCloseOnOrBefore(historicalPrices[symbol] || [], date);
  const price = close ?? holding.currentPrice;

  return (holding.units || 0) * price;
}

function estimateOptionValueOnDate(
  holding: AccountHolding,
  historicalPrices: Record<string, HistoricalClose[]>,
  date: string
): number {
  const symbol = historicalPriceSymbolForTrade(holding.optionSymbol || holding.symbol);
  const mark = getOptionMarkOnDate(historicalPrices[symbol] || [], date);
  if (!mark) return Number.NaN;

  return (holding.units || 0) * mark.close * 100;
}

function estimateHoldingValueForCurveDiagnostics(
  holding: AccountHolding,
  historicalPrices: Record<string, HistoricalClose[]>,
  date: string
): number {
  if (isTodayDateKey(date)) {
    return holding.marketValue || 0;
  }

  if (holding.isOption) {
    return estimateOptionValueOnDate(holding, historicalPrices, date);
  }

  return estimateEquityValueOnDate(holding, historicalPrices, date);
}

function describeHoldingCurveSource(
  holding: AccountHolding,
  historicalPrices: Record<string, HistoricalClose[]>,
  date: string
): string {
  if (isTodayDateKey(date)) {
    return 'current broker value';
  }

  const symbol = holding.isOption
    ? historicalPriceSymbolForTrade(holding.optionSymbol || holding.symbol)
    : normalizeHistoricalSymbol(holding.symbol);
  const history = historicalPrices[symbol] || [];
  const mark = holding.isOption
    ? getOptionMarkOnDate(history, date)
    : getMarkOnOrBefore(history, date);

  if (!holding.isOption) {
    return mark == null
      ? `current price fallback; no ${symbol} close on/before ${date}`
      : `${symbol} historical close ${mark.close}`;
  }

  return mark == null
    ? `missing option mark for ${symbol} on ${date}`
    : `${symbol} ${mark.source || 'option mark'} ${mark.close}`;
}

function isTodayDateKey(date: string): boolean {
  return date === new Date().toISOString().split('T')[0];
}

function getCloseOnOrBefore(history: HistoricalClose[], date: string): number | null {
  return getMarkOnOrBefore(history, date)?.close ?? null;
}

function getMarkOnOrBefore(history: HistoricalClose[], date: string): HistoricalClose | null {
  const sorted = [...history]
    .filter(point => point.close > 0 && point.date <= date)
    .sort((a, b) => b.date.localeCompare(a.date));

  return sorted[0] ?? null;
}

function getOptionMarkOnDate(history: HistoricalClose[], date: string): HistoricalClose | null {
  return history.find(point => point.close > 0 && point.date === date) ?? null;
}

function getOptionMarkForValuation(history: HistoricalClose[], date: string): HistoricalClose | null {
  const exact = getOptionMarkOnDate(history, date);
  if (exact) return exact;

  const valid = [...history]
    .filter(point => point.close > 0)
    .sort((a, b) => a.date.localeCompare(b.date));
  const previous = [...valid].reverse().find(point => point.date < date) || null;
  const next = valid.find(point => point.date > date) || null;

  if (previous && next) {
    const prevTime = Date.parse(`${previous.date}T00:00:00Z`);
    const targetTime = Date.parse(`${date}T00:00:00Z`);
    const nextTime = Date.parse(`${next.date}T00:00:00Z`);
    const spanDays = (nextTime - prevTime) / 86_400_000;

    if (spanDays > 0 && spanDays <= OPTION_FALLBACK_MAX_GAP_DAYS) {
      const weight = (targetTime - prevTime) / (nextTime - prevTime);
      return {
        date,
        close: previous.close + (next.close - previous.close) * weight,
        source: 'interpolated',
        confidence: 'low',
      };
    }
  }

  const targetTime = Date.parse(`${date}T00:00:00Z`);
  const nearest = [previous, next]
    .filter((point): point is HistoricalClose => Boolean(point))
    .map(point => ({
      point,
      distance: Math.abs(targetTime - Date.parse(`${point.date}T00:00:00Z`)) / 86_400_000,
    }))
    .sort((a, b) => a.distance - b.distance)[0];

  if (!nearest || nearest.distance > OPTION_FALLBACK_MAX_GAP_DAYS) return null;

  return {
    ...nearest.point,
    source: 'nearest_option_mark',
    confidence: 'low',
  };
}

function buildHistoricalPositionsFromTrades(
  tradeResults: TradeMatchingResult,
  currentHoldings: AccountHolding[],
  startKey: string
): HistoricalPosition[] {
  const openPositions = tradeResults.openLots
    .filter(lot => Math.abs(lot.remainingUnits) > UNIT_EPSILON)
    .map(lot => ({
      symbol: historicalPriceSymbolForTrade(lot.optionSymbol || lot.symbol),
      underlyingSymbol: lot.underlyingSymbol,
      isOption: lot.isOption,
      units: lot.remainingUnits,
      openDate: dateKey(lot.tradeDate),
      closeDate: null,
      name: lot.optionSymbol || lot.symbol,
    }));

  const closedPositions = tradeResults.closedTrades
    .filter(trade => trade.units > UNIT_EPSILON)
    .map(trade => ({
      symbol: historicalPriceSymbolForTrade(trade.optionSymbol || trade.symbol),
      underlyingSymbol: trade.underlyingSymbol,
      isOption: trade.isOption,
      units: inferClosedTradeSignedUnits(trade),
      openDate: dateKey(trade.openDate),
      closeDate: dateKey(trade.closeDate),
      name: trade.optionSymbol || trade.symbol,
    }));

  const currentUnitsBySymbol = new Map<string, HistoricalPosition>();
  for (const holding of currentHoldings) {
    if (holding.isCash) continue;

    const symbol = historicalPriceSymbolForTrade(holding.optionSymbol || holding.symbol);
    if (!symbol) continue;

    const existing = currentUnitsBySymbol.get(symbol) || {
      symbol,
      underlyingSymbol: holding.underlyingSymbol,
      isOption: holding.isOption,
      units: 0,
      openDate: startKey,
      closeDate: null,
      name: holding.description || holding.optionSymbol || holding.symbol,
    };
    existing.units += holding.units;
    currentUnitsBySymbol.set(symbol, existing);
  }

  const openUnitsBySymbol = new Map<string, number>();
  for (const position of openPositions) {
    openUnitsBySymbol.set(position.symbol, (openUnitsBySymbol.get(position.symbol) || 0) + position.units);
  }

  const unmatchedCurrentPositions = [...currentUnitsBySymbol.values()]
    .map(position => ({
      ...position,
      units: position.units - (openUnitsBySymbol.get(position.symbol) || 0),
    }))
    .filter(position => Math.abs(position.units) > UNIT_EPSILON);

  return [...openPositions, ...closedPositions, ...unmatchedCurrentPositions]
    .filter(position => position.symbol && Math.abs(position.units) > UNIT_EPSILON);
}

function buildCurrentHoldingRows(holdings: AccountHolding[], cash: number, date: string): EquityPointHolding[] {
  const rows: EquityPointHolding[] = [buildCashHoldingRow(cash, date)];

  for (const holding of holdings) {
    if (holding.isCash) continue;

    addHoldingRow(rows, {
      symbol: historicalPriceSymbolForTrade(holding.optionSymbol || holding.symbol),
      name: holding.description || holding.optionSymbol || holding.symbol,
      type: holding.isOption ? 'option' : 'equity',
      quantity: holding.units,
      value: holding.marketValue || 0,
      markSource: 'broker_current',
      markConfidence: 'high',
      markDate: date,
    });
  }

  return normalizeHoldingRows(rows);
}

function buildCashHoldingRow(value: number, date?: string): EquityPointHolding {
  return {
    symbol: 'CASH',
    name: 'Cash',
    type: 'cash',
    quantity: 1,
    value,
    markSource: 'broker_current',
    markConfidence: 'high',
    markDate: date || new Date().toISOString().split('T')[0],
  };
}

function buildTransferAdjustmentRow(value: number, date?: string): EquityPointHolding {
  return {
    symbol: 'TRANSFER_ADJUSTMENT',
    name: 'Transfer adjustment',
    type: 'adjustment',
    quantity: 1,
    value,
    markSource: 'broker_current',
    markConfidence: 'high',
    markDate: date || new Date().toISOString().split('T')[0],
  };
}

function addHoldingRow(rows: EquityPointHolding[], row: EquityPointHolding): void {
  const existing = rows.find(item => item.symbol === row.symbol && item.type === row.type);
  if (!existing) {
    rows.push(row);
    return;
  }

  existing.quantity += row.quantity;
  existing.value += row.value;
}

function normalizeHoldingRows(rows: EquityPointHolding[]): EquityPointHolding[] {
  return rows
    .filter(row => Math.abs(row.value) > 0.005 || Math.abs(row.quantity) > UNIT_EPSILON)
    .sort((a, b) => {
      if (a.type === 'cash' && b.type !== 'cash') return 1;
      if (b.type === 'cash' && a.type !== 'cash') return -1;
      if (a.type === 'adjustment' && b.type !== 'adjustment') return 1;
      if (b.type === 'adjustment' && a.type !== 'adjustment') return -1;
      return Math.abs(b.value) - Math.abs(a.value);
    });
}

function positionsHeldOnDate(positions: HistoricalPosition[], date: string): HistoricalPosition[] {
  return positions.filter(position =>
    position.openDate <= date && (!position.closeDate || date < position.closeDate)
  );
}

function valueHistoricalPosition(
  position: HistoricalPosition,
  historicalPrices: Record<string, HistoricalClose[]>,
  date: string
): ValuedPosition {
  const mark = position.isOption
    ? getOptionMarkForValuation(historicalPrices[position.symbol] || [], date)
    : getMarkOnOrBefore(historicalPrices[position.symbol] || [], date);

  if (!mark) {
    return {
      value: 0,
      missingSymbol: position.symbol,
      markSource: null,
      markConfidence: null,
      markDate: null,
    };
  }

  return {
    value: position.units * mark.close * (position.isOption ? 100 : 1),
    missingSymbol: null,
    markSource: mark.source || (position.isOption ? 'trade_close' : 'historical_close'),
    markConfidence: mark.confidence || (position.isOption ? 'low' : 'high'),
    markDate: mark.date,
  };
}

function inferClosedTradeSignedUnits(trade: TradeMatchingResult['closedTrades'][number]): number {
  const multiplier = trade.isOption ? 100 : 1;
  const longPnl = (trade.closePrice - trade.openPrice) * trade.units * multiplier;
  const shortPnl = (trade.openPrice - trade.closePrice) * trade.units * multiplier;

  if (Math.abs(trade.realizedPnl - longPnl) <= 0.01) return trade.units;
  if (Math.abs(trade.realizedPnl - shortPnl) <= 0.01) return -trade.units;

  const action = String(trade.optionType || '').toUpperCase();
  if (action === 'BUY_TO_CLOSE') return -trade.units;
  if (action === 'SELL_TO_CLOSE') return trade.units;

  return trade.units;
}

function estimateCashOnDate(currentCash: number, activities: AccountActivity[], date: string): number {
  return activities
    .filter(activity => dateKey(activity.tradeDate) > date)
    .reduce((cashOnDate, activity) => cashOnDate - getActivityCashEffect(activity), currentCash);
}

function getActivityCashEffect(activity: AccountActivity): number {
  const type = String(activity.type || '').toUpperCase();
  const actionType = String(activity.optionType || '').toUpperCase();
  const amount = getActivityDollarAmount(activity);

  if (type === 'BUY' || actionType === 'BUY_TO_OPEN' || actionType === 'BUY_TO_CLOSE') return -amount;
  if (type === 'SELL' || actionType === 'SELL_TO_OPEN' || actionType === 'SELL_TO_CLOSE') return amount;
  if (type === 'DEPOSIT' || type === 'CONTRIBUTION' || type === 'EXTERNAL_ASSET_TRANSFER_IN') return amount;
  if (type === 'WITHDRAWAL' || type === 'DISTRIBUTION' || type === 'EXTERNAL_ASSET_TRANSFER_OUT') return -amount;
  if (type === 'DIVIDEND' || type === 'INTEREST') return amount;
  if (type === 'FEE' || type === 'TAX') return -amount;

  return finiteNumber(activity.amount);
}

function getActivityDollarAmount(activity: AccountActivity): number {
  const rawAmount = Math.abs(finiteNumber(activity.amount));
  if (rawAmount > 0) return rawAmount;

  const multiplier = activity.optionSymbol ? 100 : 1;
  return Math.abs(finiteNumber(activity.units) * finiteNumber(activity.price) * multiplier);
}

function historicalPriceSymbolForTrade(symbol: string | null): string {
  return (symbol || '').trim().replace(/\s+/g, '').replace(/^O:/i, '').toUpperCase();
}

function dateKey(date: string): string {
  return date.includes('T') ? date.split('T')[0] : date;
}

function normalizeHistoricalSymbol(symbol: string | null): string {
  return (symbol || '').toUpperCase().replace(/^\./, '');
}

/**
 * Calculate daily P&L from equity curve
 */
export function calculateDailyPL(equityCurve: EquityPoint[]): DailyPLPoint[] {
  if (equityCurve.length < 2) return [];

  return equityCurve.slice(1).map((point, i) => ({
    date: point.date,
    pnl: point.value - equityCurve[i].value,
  }));
}

export function calculateCorrelationMatrix(
  symbols: string[],
  historicalPrices: Record<string, HistoricalClose[]>,
  lookbackDays = 90
): CorrelationMatrix {
  const labels = symbols
    .map(symbol => symbol.toUpperCase())
    .filter((symbol, index, all) => all.indexOf(symbol) === index)
    .filter(symbol => (historicalPrices[symbol] || []).length >= 6)
    .slice(0, 10);

  const returnsBySymbol = new Map<string, Map<string, number>>();
  for (const symbol of labels) {
    const history = historicalPrices[symbol] || [];
    const recentHistory = [...history]
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, lookbackDays + 1);
    returnsBySymbol.set(symbol, calculateReturnsByDate(recentHistory));
  }

  const values = labels.map((left, rowIndex) =>
    labels.map((right, colIndex) => {
      if (rowIndex === colIndex) return 1;
      const leftReturns = returnsBySymbol.get(left);
      const rightReturns = returnsBySymbol.get(right);
      if (!leftReturns || !rightReturns) return null;
      return calculatePearsonCorrelation(leftReturns, rightReturns);
    })
  );

  return { labels, values, lookbackDays };
}

export function buildBenchmarkCurve(
  equityCurve: EquityPoint[],
  spyHistory: HistoricalClose[] = []
): EquityPoint[] {
  if (equityCurve.length < 2 || spyHistory.length < 2) return [];

  const sortedSpy = [...spyHistory].sort((a, b) => a.date.localeCompare(b.date));
  let spyIndex = 0;
  let firstSpyClose: number | null = null;
  let firstPortfolioValue: number | null = null;
  const points: EquityPoint[] = [];

  for (const point of equityCurve) {
    while (spyIndex < sortedSpy.length - 1 && sortedSpy[spyIndex + 1].date <= point.date) {
      spyIndex += 1;
    }

    const spyPoint = sortedSpy[spyIndex];
    if (!spyPoint || spyPoint.date > point.date || spyPoint.close <= 0) continue;

    if (firstSpyClose == null) {
      firstSpyClose = spyPoint.close;
      firstPortfolioValue = point.value;
    }

    if (firstSpyClose > 0 && firstPortfolioValue != null) {
      points.push({
        date: point.date,
        value: firstPortfolioValue * (spyPoint.close / firstSpyClose),
      });
    }
  }

  return points;
}

export function calculateBeta(equityCurve: EquityPoint[], benchmarkCurve: EquityPoint[]): number | null {
  const portfolioValues = new Map(equityCurve.map(point => [point.date, point.value]));
  const pairedPortfolio: number[] = [];
  const pairedBenchmark: number[] = [];

  for (const point of benchmarkCurve) {
    const portfolioValue = portfolioValues.get(point.date);
    if (portfolioValue != null) {
      pairedPortfolio.push(portfolioValue);
      pairedBenchmark.push(point.value);
    }
  }

  if (pairedPortfolio.length < 10) return null;

  const portfolioReturns = calculateReturns(pairedPortfolio);
  const benchmarkReturns = calculateReturns(pairedBenchmark);
  if (portfolioReturns.length !== benchmarkReturns.length || benchmarkReturns.length < 2) return null;

  const benchmarkMean = average(benchmarkReturns);
  const portfolioMean = average(portfolioReturns);
  let covariance = 0;
  let benchmarkVariance = 0;

  for (let i = 0; i < benchmarkReturns.length; i++) {
    const benchmarkDiff = benchmarkReturns[i] - benchmarkMean;
    covariance += (portfolioReturns[i] - portfolioMean) * benchmarkDiff;
    benchmarkVariance += benchmarkDiff * benchmarkDiff;
  }

  return benchmarkVariance > 0 ? covariance / benchmarkVariance : null;
}

function calculateReturnsByDate(history: HistoricalClose[]): Map<string, number> {
  const sorted = [...history]
    .filter(point => point.close > 0)
    .sort((a, b) => a.date.localeCompare(b.date));
  const returns = new Map<string, number>();

  for (let i = 1; i < sorted.length; i++) {
    const previous = sorted[i - 1];
    const current = sorted[i];
    if (previous.close > 0) {
      returns.set(current.date, Math.log(current.close / previous.close));
    }
  }

  return returns;
}

function calculatePearsonCorrelation(
  leftReturns: Map<string, number>,
  rightReturns: Map<string, number>
): number | null {
  const left: number[] = [];
  const right: number[] = [];

  for (const [date, value] of leftReturns) {
    const matching = rightReturns.get(date);
    if (matching != null) {
      left.push(value);
      right.push(matching);
    }
  }

  if (left.length < 5) return null;

  const leftMean = average(left);
  const rightMean = average(right);
  let numerator = 0;
  let leftSumSquares = 0;
  let rightSumSquares = 0;

  for (let i = 0; i < left.length; i++) {
    const leftDiff = left[i] - leftMean;
    const rightDiff = right[i] - rightMean;
    numerator += leftDiff * rightDiff;
    leftSumSquares += leftDiff * leftDiff;
    rightSumSquares += rightDiff * rightDiff;
  }

  const denominator = Math.sqrt(leftSumSquares * rightSumSquares);
  return denominator > 0 ? numerator / denominator : null;
}

function average(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

/**
 * Calculate portfolio metrics from equity curve and trade results
 */
export function calculateMetrics(
  equityCurve: EquityPoint[],
  tradeResults?: TradeMatchingResult
): PortfolioMetrics {
  const values = equityCurve.map(p => p.value);

  // Sharpe Ratio (simplified: assuming risk-free rate of 0)
  let sharpeRatio: number | null = null;
  if (values.length >= 30) {
    const returns = calculateReturns(values);
    const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
    const stdDev = Math.sqrt(
      returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / (returns.length - 1)
    );
    if (stdDev > 0) {
      // Annualize: multiply by sqrt(252) for daily data
      sharpeRatio = (avgReturn / stdDev) * Math.sqrt(252);
    }
  }

  // Volatility (annualized)
  let volatility: number | null = null;
  if (values.length >= 30) {
    const returns = calculateReturns(values);
    const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / (returns.length - 1);
    volatility = Math.sqrt(variance) * Math.sqrt(252) * 100; // As percentage
  }

  // Max Drawdown
  let maxDrawdown: number | null = null;
  if (values.length >= 2) {
    let peak = values[0];
    let maxDd = 0;

    for (const value of values) {
      if (value > peak) {
        peak = value;
      }
      const dd = (peak - value) / peak;
      if (dd > maxDd) {
        maxDd = dd;
      }
    }

    maxDrawdown = maxDd * 100; // As percentage
  }

  // Win Rate from trade results
  let winRate: number | null = null;
  if (tradeResults) {
    const { totalWins, totalLosses } = tradeResults.summary;
    const total = totalWins + totalLosses;
    if (total > 0) {
      winRate = (totalWins / total) * 100;
    }
  }

  return {
    sharpeRatio,
    volatility,
    maxDrawdown,
    winRate,
    beta: null, // Would need market data to calculate
  };
}

function calculateReturns(values: number[]): number[] {
  const returns: number[] = [];
  for (let i = 1; i < values.length; i++) {
    if (values[i - 1] > 0) {
      returns.push((values[i] - values[i - 1]) / values[i - 1]);
    }
  }
  return returns;
}

/**
 * Calculate realized P&L YTD from trade results
 */
export function calculateRealizedYTD(tradeResults: TradeMatchingResult): number {
  const yearStart = new Date();
  yearStart.setMonth(0, 1);
  yearStart.setHours(0, 0, 0, 0);

  return tradeResults.closedTrades
    .filter(t => new Date(t.closeDate) >= yearStart && !t.missingCostBasis)
    .reduce((sum, t) => sum + t.realizedPnl, 0);
}
